import React from 'react'
import { WemoloToggle, WemoloButton, WemoloDropdown, WemoloTypography } from '../../components/Wemolo'
import styles from './AccessControlDeviceDetailUI.module.css'

export interface StationOption {
    label: string
    value: string
}

export interface AccessControlDeviceDetailUIProps {
    deviceId: string | null
    deviceName: string
    onBackClick: () => void
    onSaveClick: () => void
    onTestDeviceClick: () => void
    lpAccessEnabled: boolean
    onLpAccessToggle: (enabled: boolean) => void
    selectedStation: string
    onStationChange: (station: string) => void
    stationOptions: StationOption[]
    stationsLoading: boolean
    lotLive: boolean
    isSwitchTriggering: boolean
    isSaving: boolean
}

export const AccessControlDeviceDetailUI: React.FC<AccessControlDeviceDetailUIProps> = ({
    deviceId,
    deviceName,
    onBackClick,
    onSaveClick,
    onTestDeviceClick,
    lpAccessEnabled,
    onLpAccessToggle,
    selectedStation,
    onStationChange,
    stationOptions,
    stationsLoading,
    lotLive,
    isSwitchTriggering,
    isSaving
}) => {
    return (
        <div className={styles.container}>
            <div className={styles.inner}>
                <div className={styles.header}>
                    <div className={styles.backButton} onClick={onBackClick}>
                        <WemoloTypography variant="body-small" tag="span">
                            ← Back to Lot Details
                        </WemoloTypography>
                    </div>
                </div>

                <div className={styles.deviceCard}>
                    <div className={styles.deviceCardContent}>
                        <div className={styles.deviceBadgeContainer}>
                            <div className={styles.acBadge}>
                                <WemoloTypography variant="title-large" tag="span">
                                    AC
                                </WemoloTypography>
                            </div>
                            <div className={styles.deviceInfo}>
                                <WemoloTypography
                                    variant="body-medium-emphasized"
                                    tag="p"
                                    className={styles.deviceName}
                                >
                                    {deviceName}
                                </WemoloTypography>
                                <WemoloTypography variant="body-small" tag="p" className={styles.deviceId}>
                                    {deviceId}
                                </WemoloTypography>
                            </div>
                        </div>
                        <WemoloButton
                            onClick={onTestDeviceClick}
                            className={styles.triggerButton}
                            disabled={isSwitchTriggering}
                        >
                            {isSwitchTriggering ? 'Triggering...' : 'Test Opening'}
                        </WemoloButton>
                    </div>
                </div>

                <div className={styles.contentCard}>
                    <div className={styles.section}>
                        <WemoloTypography variant="title-medium" tag="h2" className={styles.sectionTitle}>
                            Device Configuration
                        </WemoloTypography>

                        <div className={styles.configCard}>
                            <div className={styles.configHeader}>
                                <div className={styles.configTitle}>
                                    <WemoloTypography
                                        variant="body-medium-emphasized"
                                        tag="p"
                                        className={styles.configName}
                                    >
                                        LP-based Access Control
                                    </WemoloTypography>
                                    <WemoloTypography variant="body-small" tag="p" className={styles.configDescription}>
                                        When active, authorized plates open the shutter automatically.
                                    </WemoloTypography>
                                </div>
                                <WemoloToggle
                                    checked={lpAccessEnabled}
                                    onChange={(e) => onLpAccessToggle(e.target.checked)}
                                />
                            </div>
                        </div>

                        {lpAccessEnabled && !lotLive && (
                            <WemoloTypography variant="body-small" tag="p" className={styles.warningText}>
                                This feature won&apos;t work until lot is taken live
                            </WemoloTypography>
                        )}
                    </div>

                    {lpAccessEnabled && (
                        <div className={styles.section}>
                            <WemoloTypography
                                variant={'body-small-emphasized'}
                                tag="p"
                                className={styles.deviceMappingLabel}
                            >
                                Scanner Assignment
                            </WemoloTypography>

                            <div className={styles.mappingGroup}>
                                <WemoloDropdown
                                    value={selectedStation}
                                    onChange={onStationChange}
                                    placeholder={stationsLoading ? 'Loading stations...' : 'Select station...'}
                                    className={styles.dropdown}
                                    options={stationOptions}
                                />
                            </div>
                        </div>
                    )}

                    <div className={styles.buttonGroup}>
                        <WemoloButton
                            onClick={onSaveClick}
                            disabled={isSaving || (lpAccessEnabled && !selectedStation)}
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </WemoloButton>
                    </div>
                </div>
            </div>
        </div>
    )
}
